import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  Activity, RefreshCw, CheckCircle2, AlertTriangle, XCircle,
  Clock, Zap, Server, Shield, Users, MapPin, MessageSquare,
  FileText, Crown, Database, Wifi, ChevronDown, ChevronRight,
  Search, Filter
} from 'lucide-react';

const API = 'https://api.lifeshare.app/api/v1';

interface Feature {
  category: string;
  feature: string;
  status: 'ok' | 'warning' | 'error';
  ms: number;
  detail: string;
  method?: string;
  url?: string;
}

interface Summary {
  total: number;
  ok: number;
  warning: number;
  error: number;
  avg_response_ms: number;
  checked_at: string;
}

const categoryIcons: Record<string, React.ElementType> = {
  'Altyapı': Server,
  'Kimlik Doğrulama': Shield,
  'Kullanıcı Yönetimi': Users,
  'Çevre': Users,
  'Konum & Harita': MapPin,
  'Güvenlik': Shield,
  'İçerik': FileText,
  'İletişim': MessageSquare,
  'Veri & Raporlar': Activity,
  'Canlı Aktivite': Zap,
};

const categoryColors: Record<string, string> = {
  'Altyapı': 'from-blue-500 to-blue-600',
  'Kimlik Doğrulama': 'from-purple-500 to-purple-600',
  'Kullanıcı Yönetimi': 'from-indigo-500 to-indigo-600',
  'Çevre': 'from-pink-500 to-pink-600',
  'Konum & Harita': 'from-green-500 to-green-600',
  'Güvenlik': 'from-red-500 to-red-600',
  'İçerik': 'from-amber-500 to-amber-600',
  'İletişim': 'from-teal-500 to-teal-600',
  'Veri & Raporlar': 'from-violet-500 to-violet-600',
  'Canlı Aktivite': 'from-emerald-500 to-emerald-600',
};

function StatusBadge({ status }: { status: string }) {
  if (status === 'ok') return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-semibold">
      <CheckCircle2 className="w-3.5 h-3.5" /> Çalışıyor
    </span>
  );
  if (status === 'warning') return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-50 text-amber-700 rounded-full text-xs font-semibold">
      <AlertTriangle className="w-3.5 h-3.5" /> Uyarı
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-50 text-red-700 rounded-full text-xs font-semibold">
      <XCircle className="w-3.5 h-3.5" /> Hata
    </span>
  );
}

function MsBar({ ms }: { ms: number }) {
  if (ms === 0) return null;
  const width = Math.min(100, (ms / 2000) * 100);
  const color = ms < 300 ? 'bg-emerald-400' : ms < 1000 ? 'bg-amber-400' : 'bg-red-400';
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${width}%` }} />
      </div>
      <span className="text-xs text-gray-500 font-mono w-12 text-right">{ms}ms</span>
    </div>
  );
}

export default function FeatureStatus() {
  const { token } = useAuth();
  const [data, setData] = useState<{ summary: Summary; features: Feature[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedCats, setExpandedCats] = useState<Set<string>>(new Set());
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false);

  const fetchStatus = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await axios.get(`${API}/admin/feature-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
      // Hata olan kategorileri otomatik aç
      if (!silent) {
        const errorCats = new Set<string>();
        res.data.features.forEach((f: Feature) => {
          if (f.status === 'error' || f.status === 'warning') errorCats.add(f.category);
        });
        setExpandedCats(errorCats);
      }
    } catch (err) {
      console.error('Feature status fetch error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => fetchStatus(true), 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchStatus]);

  const toggleCat = (cat: string) => {
    setExpandedCats(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  };

  const expandAll = () => {
    if (!data) return;
    const allCats = new Set(data.features.map(f => f.category));
    setExpandedCats(allCats);
  };

  const collapseAll = () => setExpandedCats(new Set());

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-32 gap-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600" />
        <p className="text-gray-500 text-sm">Tüm özellikler kontrol ediliyor...</p>
        <p className="text-gray-400 text-xs">Bu işlem 10-15 saniye sürebilir</p>
      </div>
    );
  }

  if (!data) return <div className="py-20 text-center text-gray-500">Veri yüklenemedi</div>;

  const { summary, features } = data;

  // Filtreleme
  const filtered = features.filter(f => {
    if (filterStatus !== 'all' && f.status !== filterStatus) return false;
    if (search && !f.feature.toLowerCase().includes(search.toLowerCase()) && !f.category.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // Kategorilere grupla
  const grouped: Record<string, Feature[]> = {};
  filtered.forEach(f => {
    if (!grouped[f.category]) grouped[f.category] = [];
    grouped[f.category].push(f);
  });

  const catOrder = ['Altyapı', 'Canlı Aktivite', 'Kimlik Doğrulama', 'Kullanıcı Yönetimi', 'Çevre', 'Konum & Harita', 'Güvenlik', 'İçerik', 'İletişim', 'Veri & Raporlar'];
  const sortedCats = Object.keys(grouped).sort((a, b) => {
    const ai = catOrder.indexOf(a);
    const bi = catOrder.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  const healthPercent = summary.total > 0 ? Math.round((summary.ok / summary.total) * 100) : 0;
  const healthColor = healthPercent >= 90 ? 'text-emerald-600' : healthPercent >= 70 ? 'text-amber-600' : 'text-red-600';
  const healthBg = healthPercent >= 90 ? 'from-emerald-500 to-emerald-600' : healthPercent >= 70 ? 'from-amber-500 to-amber-600' : 'from-red-500 to-red-600';

  return (
    <div className="space-y-6">
      <div className="max-w-6xl mx-auto space-y-6">
      {/* Başlık */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">Özellik Durumu</h1>
          <p className="text-sm text-gray-500 mt-1">
            Tüm API endpoint'leri ve servislerin gerçek zamanlı durumu
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`px-3 py-2 rounded-full text-xs font-medium transition-all ${autoRefresh ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            {autoRefresh ? '⏸ Otomatik: Açık (30s)' : '▶ Otomatik Yenile'}
          </button>
          <button
            onClick={() => fetchStatus(true)}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-full text-sm font-medium transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Kontrol...' : 'Yenile'}
          </button>
        </div>
      </div>

      {/* Özet Kartları */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {/* Sağlık Skoru */}
        <div className={`bg-gradient-to-br ${healthBg} rounded-2xl p-5 text-white col-span-1`}>
          <div className="flex items-center gap-2 mb-2 opacity-90">
            <Activity className="w-4 h-4" />
            <span className="text-xs font-medium">Sistem Sağlığı</span>
          </div>
          <p className="text-3xl font-bold">%{healthPercent}</p>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <div className="flex items-center gap-2 mb-2 text-gray-400">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            <span className="text-xs font-medium">Çalışıyor</span>
          </div>
          <p className="text-2xl font-bold text-emerald-600">{summary.ok}</p>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <div className="flex items-center gap-2 mb-2 text-gray-400">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <span className="text-xs font-medium">Uyarı</span>
          </div>
          <p className="text-2xl font-bold text-amber-600">{summary.warning}</p>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <div className="flex items-center gap-2 mb-2 text-gray-400">
            <XCircle className="w-4 h-4 text-red-500" />
            <span className="text-xs font-medium">Hata</span>
          </div>
          <p className="text-2xl font-bold text-red-600">{summary.error}</p>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <div className="flex items-center gap-2 mb-2 text-gray-400">
            <Zap className="w-4 h-4 text-blue-500" />
            <span className="text-xs font-medium">Ort. Yanıt</span>
          </div>
          <p className="text-2xl font-bold text-blue-600">{summary.avg_response_ms}<span className="text-sm font-normal text-gray-400 ml-1">ms</span></p>
        </div>
      </div>

      {/* Kontrol zamanı */}
      <div className="flex items-center justify-between text-xs text-gray-400">
        <span>Son kontrol: {new Date(summary.checked_at).toLocaleString('tr-TR')}</span>
        <span>{summary.total} özellik kontrol edildi</span>
      </div>

      {/* Filtreler */}
      <div className="bg-white rounded-2xl p-4 border border-gray-100">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Özellik ara..."
              className="w-full pl-10 pr-4 py-2 bg-gray-50 rounded-xl text-sm border-0 focus:ring-2 focus:ring-purple-200 focus:bg-white transition-all"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-4 h-4 text-gray-400" />
            {[
              { key: 'all', label: 'Tümü', count: features.length },
              { key: 'ok', label: 'Çalışıyor', count: summary.ok },
              { key: 'warning', label: 'Uyarı', count: summary.warning },
              { key: 'error', label: 'Hata', count: summary.error },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setFilterStatus(f.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${filterStatus === f.key
                  ? f.key === 'error' ? 'bg-red-100 text-red-700' : f.key === 'warning' ? 'bg-amber-100 text-amber-700' : f.key === 'ok' ? 'bg-emerald-100 text-emerald-700' : 'bg-purple-100 text-purple-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f.label} ({f.count})
              </button>
            ))}
            <span className="text-gray-300 mx-1">|</span>
            <button onClick={expandAll} className="text-xs text-purple-600 hover:underline">Hepsini Aç</button>
            <button onClick={collapseAll} className="text-xs text-gray-500 hover:underline">Kapat</button>
          </div>
        </div>
      </div>

      {/* Hata özeti (varsa) */}
      {summary.error > 0 && filterStatus === 'all' && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
          <div className="flex items-start gap-3">
            <XCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-red-800">{summary.error} özellikte hata tespit edildi</p>
              <div className="mt-2 space-y-1">
                {features.filter(f => f.status === 'error').map((f, i) => (
                  <p key={i} className="text-xs text-red-700">
                    <span className="font-medium">{f.feature}</span> — <span className="font-mono">{f.detail}</span>
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Kategori Listesi */}
      <div className="space-y-3">
        {sortedCats.map(cat => {
          const items = grouped[cat];
          const isOpen = expandedCats.has(cat);
          const catOk = items.filter(i => i.status === 'ok').length;
          const catErr = items.filter(i => i.status === 'error').length;
          const catWarn = items.filter(i => i.status === 'warning').length;
          const Icon = categoryIcons[cat] || Activity;
          const gradient = categoryColors[cat] || 'from-gray-500 to-gray-600';

          return (
            <div key={cat} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              {/* Kategori Başlığı */}
              <button
                onClick={() => toggleCat(cat)}
                className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors"
              >
                <div className={`w-10 h-10 bg-gradient-to-br ${gradient} rounded-xl flex items-center justify-center flex-shrink-0`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-semibold text-gray-900">{cat}</p>
                  <p className="text-xs text-gray-400">{items.length} özellik</p>
                </div>
                <div className="flex items-center gap-3">
                  {catOk > 0 && <span className="text-xs text-emerald-600 font-medium bg-emerald-50 px-2 py-0.5 rounded-full">{catOk} ✓</span>}
                  {catWarn > 0 && <span className="text-xs text-amber-600 font-medium bg-amber-50 px-2 py-0.5 rounded-full">{catWarn} ⚠</span>}
                  {catErr > 0 && <span className="text-xs text-red-600 font-medium bg-red-50 px-2 py-0.5 rounded-full">{catErr} ✗</span>}
                  {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                </div>
              </button>

              {/* Özellik Listesi */}
              {isOpen && (
                <div className="border-t border-gray-50">
                  {items.map((item, i) => (
                    <div key={i} className={`flex items-center gap-4 px-4 py-3 ${i > 0 ? 'border-t border-gray-50' : ''} hover:bg-gray-50/50 transition-colors`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{item.feature}</p>
                        <p className="text-xs text-gray-400 font-mono truncate mt-0.5">{item.detail}</p>
                      </div>
                      <MsBar ms={item.ms} />
                      <StatusBadge status={item.status} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Filtrelerle eşleşen özellik bulunamadı</p>
        </div>
      )}
    </div>
    </div>
  );
}
